import React, { useMemo, useState } from "react";
import { Grid3x3 } from "lucide-react";

import { formatCurrency } from "@/features/relatorio-vendas/lib/formato";

import { MODULOS_MAPA } from "../modulos";
import { DIAS_SEMANA, NOME_FAIXA, formatarDecimal, horasDoMapa, porHoraDosDias, reaisInteiros } from "../regras";
import { FiltroDias, Secao, Segmentado } from "./Base";
import IconeModulo from "./IconeModulo";
import QuadroCalor from "./QuadroCalor";

/**
 * Faturamento hora a hora de cada módulo (pedido do dono, 27/10/2026): valor em
 * reais na célula e a cor pelo pico DAQUELE módulo. Só o que aconteceu: sem
 * "esperado". Dá para ver o período somado ou a média de um dia, e filtrar os
 * dias da semana (ex.: só sexta e sábado).
 */
const MODOS = [
  { key: "periodo", rotulo: "Somado no período" },
  { key: "dia", rotulo: "Média de um dia" },
];
const num = (v) => Number(v) || 0;
const pct = (v) => `${Math.round(v * 100)}%`;

function montarLinhas(todas) {
  return (key, nome, mod) => ({
    key, nome,
    rotulo: mod
      ? <span className="flex items-center gap-2 text-left">
        <IconeModulo modulo={mod} tamanho="w-7 h-7" icone="w-4 h-4" />
        <span className="hidden sm:inline">{nome}</span>
      </span>
      : null,
    valores: Object.fromEntries(todas.map((h) => [h.hora, num(h[key])])),
    contagens: Object.fromEntries(todas.map((h) => [h.hora, num(h.pedidos?.[key])])),
    total: todas.reduce((s, h) => s + num(h[key]), 0),
    pedidos: todas.reduce((s, h) => s + num(h.pedidos?.[key]), 0),
  });
}

export default function MapaHoras({ dados: bruto }) {
  const [modo, setModo] = useState("periodo");
  const [diasSel, setDiasSel] = useState([]);
  const dados = useMemo(() => porHoraDosDias(bruto, diasSel), [bruto, diasSel]);
  const { horas, linhas, total } = useMemo(() => {
    const todas = dados?.horas || [];
    const linha = montarLinhas(todas);
    return {
      horas: horasDoMapa(todas),
      linhas: (dados?.modulos || []).map((m) => {
        const mod = MODULOS_MAPA[m.key] || { nome: m.nome, Icon: Grid3x3 };
        return linha(m.key, mod.nome, mod);
      }),
      total: linha("total", "Total"),
    };
  }, [dados]);
  if (!bruto?.horas?.length) return null;

  const dias = Math.max(1, num(dados.dias));
  const div = modo === "dia" ? dias : 1;
  const reais = (v) => reaisInteiros(v / div);
  const qtd = (n) => (modo === "dia" ? formatarDecimal(n / div) : String(n));
  const quais = diasSel.length ? ` · só ${diasSel.map((d) => DIAS_SEMANA[d]).join(", ")}` : "";
  const subModo = (modo === "dia" ? `média de um dia (${dias} ${dias === 1 ? "dia" : "dias"} com venda)` : "somado no período") + quais;
  const porHoraTotal = Object.fromEntries((dados.horas || []).map((h) => [h.hora, h]));

  return (
    <Secao titulo="Faturamento hora a hora" icone={Grid3x3} e2e="mapa-horas"
      explicacao={modo === "dia"
        ? `Quanto cada módulo fatura, em média, num dia de venda, em cada hora (o período dividido pelos ${dias} ${dias === 1 ? "dia" : "dias"} com venda).`
        : "Quanto cada módulo faturou em cada hora do dia, somando o período. A cor compara cada hora com a melhor hora daquele módulo."}
      direita={<Segmentado opcoes={MODOS} valor={modo} aoMudar={setModo} e2e="modo-mapa" />}>
      <div className="mb-3"><FiltroDias valor={diasSel} aoMudar={setDiasSel} /></div>
      {!dados.horas?.length
        ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma venda nesses dias da semana.</p>
        : (
          <QuadroCalor key={`${modo}-${diasSel.join()}`} horas={horas} linhas={linhas} total={total}
            celula={(v) => reais(v)}
            totalCelula={(l) => ({ valor: reais(l.total), sub: l.key !== "total" && total.total > 0 ? pct(l.total / total.total) : null })}
            dica={(l, h, f) => {
              const v = num(l.valores[h.hora]);
              const ped = num(l.contagens[h.hora]);
              const itens = [["Faturamento", formatCurrency(v / div)], ["Pedidos", qtd(ped)]];
              if (ped) itens.push(["Ticket médio", formatCurrency(v / ped)]);
              itens.push(["Faixa", `${pct(f.pct)} do pico (${NOME_FAIXA[f.faixa]})${f.pouco ? ", menos de 5 pedidos" : ""}`]);
              const daHora = num(porHoraTotal[h.hora]?.total);
              if (l.key !== "total" && daHora) itens.push(["Parte da hora", `${pct(v / daHora)} do faturamento`]);
              return { sub: `${l.nome} · ${subModo}`, itens };
            }}
            dicaTotal={(l) => ({ sub: subModo, itens: [
              ["Faturamento", formatCurrency(l.total / div)], ["Pedidos", qtd(l.pedidos)],
              ...(l.key !== "total" && total.total ? [["Parte do total", pct(l.total / total.total)]] : []),
            ] })}
            barras={{
              rotulo: "Faturamento", valores: total.valores, total: reais(total.total),
              dica: (h) => ({ sub: subModo, itens: [
                ["Faturamento", formatCurrency(num(total.valores[h.hora]) / div)], ["Pedidos", qtd(num(total.contagens[h.hora]))],
                ["Do dia todo", pct(num(total.valores[h.hora]) / (total.total || 1))],
              ] }),
            }}
            legenda="Cor = % da melhor hora do módulo:" />
        )}
    </Secao>
  );
}
